import { IApiService, ContextPart, IContext } from '@/api';
import {
    RelocationLimit, Channel, Site, EditedRelocationLimit,
} from '.';
import { RelocationLimitsTableData } from './RelocationLimitsTableData';
import { DeleteRelocationLimitsContext } from './DeleteRelocationLimitsContext';
import { SaveRelocationLimitsContext } from './SaveRelocationLimitsContext';

export class ComponentData {

    public sites: Site[] = [];
    public channels: Channel[] = [];

    public promiseLoadChannels: Promise<Channel[]> | null = null;

    public tableData: RelocationLimitsTableData;

    constructor() {
        this.tableData = new RelocationLimitsTableData();
    }

    public load(apiService: IApiService): void {
        // Starts loading Sites
        const taskLoadSites = this.loadSites(apiService);
        // Starts loading Channels
        const taskLoadChannels = this.loadChannels(apiService);
        // Starts loading Relocation Limits
        const taskLoadRelocationLimits = this.loadRelocationLimits(apiService);

        // Results of loading
        let sites: Site[];
        let channels: Channel[];
        let relocationLimits: RelocationLimit[];

        // The task of materializing Sites
        // Materializing is converting from DTO objects to the page objects.
        const taskMaterializeSites = taskLoadSites
            .then((context) => this.materializeSites(context))
        ;

        // The task of materializing Channels
        // Materializing is converting from DTO objects to the page objects.
        const taskMaterializeChannels = taskLoadChannels
            .then(async (channelsDto) => {
                // We need Sites to materialize Channels
                sites = await taskMaterializeSites;
                return this.materializeChannels(channelsDto, sites);
            })
        ;

        // The task of materializing Relocation Limits
        // Materializing is converting from DTO objects to the page objects.
        const taskMaterializeRelocationLimits = taskLoadRelocationLimits
            .then(async (relocationLimitsDto) => {
                // We need Channels to materialize Relocation Limits
                channels = await taskMaterializeChannels;
                relocationLimits = this.materializeRelocationLimits(relocationLimitsDto, channels);
            })
        ;

        // The task of storing loading results in the ComponentData
        const taskLoad = taskMaterializeRelocationLimits
            .then(() => {
                // Remove old data
                this.sites.splice(0, this.sites.length);
                // Push new data
                this.sites.push(...sites);

                // Remove old data
                this.channels.splice(0, this.channels.length);
                // Push new data
                this.channels.push(...channels);

                const tableData = this.tableData;
                // Remove old data
                tableData.relocationLimits.splice(0, tableData.relocationLimits.length);
                // Push new data
                tableData.relocationLimits.push(...relocationLimits);
            })
        ;

        // Stores tasks in the ComponentData
        this.tableData.promiseLoad = taskLoad;
        this.promiseLoadChannels = taskMaterializeChannels;
    }
    public async delete(apiService: IApiService,
                        relocationLimits: RelocationLimit[]): Promise<void> {
        if (relocationLimits.length > 0) {
            // Converts the collection of Relocation Limits to JSON to send to the backend
            const context = new DeleteRelocationLimitsContext(relocationLimits);

            await apiService.postContext(context);
        }
    }
    public async save(
        apiService: IApiService,
        relocationLimit: EditedRelocationLimit
    ): Promise<void> {
        const context = new SaveRelocationLimitsContext([relocationLimit]);

        await apiService.postContext(context);
    }

    private async loadSites(apiService: IApiService): Promise<IContext> {
        const result = await apiService.getContextPart(ContextPart.Sites);
        return result;
    }
    private materializeSites(context: IContext): Site[] {
        if (!context) { throw new Error('context is not set'); }
        if (!context.sites) { throw new Error('context.sites is not set'); }

        const result = [];

        for (const siteDto of context.sites) {
            const site = Site.fromDto(siteDto);
            result.push(site);
        }

        return result;
    }

    private async loadChannels(apiService: IApiService): Promise<IContext> {
        const result = apiService.getContextPart(ContextPart.Channels);
        return result;
    }
    private materializeChannels(context: IContext, sites: Site[]): Channel[] {
        if (!context) { throw new Error('context is not set'); }
        if (!context.channels) { throw new Error('context.channels is not set'); }
        if (!sites) { throw new Error('sites is not set'); }

        const result = [];

        for (const channelDto of context.channels) {
            const channel = Channel.fromDto(channelDto, sites);
            result.push(channel);
        }

        return result;
    }

    private async loadRelocationLimits(apiService: IApiService): Promise<IContext> {
        const result = await apiService.getContextPart(ContextPart.RelocationLimits);
        return result;
    }
    private materializeRelocationLimits(
        context: IContext, channels: Channel[]
    ): RelocationLimit[] {
        if (!context) { throw new Error('context is not set'); }
        if (!context.relocationLimits) {
            throw new Error('context.relocationLimits is not set');
        }
        if (!channels) { throw new Error('channels is not set'); }

        const result = [];

        for (const relocationLimitDto of context.relocationLimits) {
            const relocationLimit = RelocationLimit.fromDto(relocationLimitDto, channels);
            result.push(relocationLimit);
        }

        return result;
    }

}
